<template>
  <div data-private>
   <PageHeader title="Campaigns" :breadcrumbs="[{ label: 'Beta' }, { label: 'Outreach' }]" >
    <template #actions>
      <button @click="openCreateCampaign" class="ml-auto btn-primary">
        Create Campaign
      </button>
    </template>
  </PageHeader>
  
  <div class="p-4">
  
    <div class="grid grid-cols-1 gap-6" :class="selectedCampaign ? 'lg:grid-cols-5' : ''">
      <!-- Left Column: Campaigns List -->
      <div :class="selectedCampaign ? 'lg:col-span-2' : ''">
        <!-- Campaigns List -->
        <div class="bg-white rounded-lg border" style="height: 664px; display: flex; flex-direction: column;">
          <ComponentHeader title="Campaigns" subtitle="Click to select" />
          <ag-grid-vue
            style="width: 100%; flex: 1;"
            :theme="theme"
            :columnDefs="campaignColumnDefs"
            :rowData="campaigns"
            :defaultColDef="defaultColDef"
            :grid-options="{ suppressCellFocus: true }"
            @grid-ready="onCampaignGridReady"
            @row-clicked="onCampaignClicked"
          />
        </div>
      </div>

      <!-- Right Column: Campaign Tasks -->
      <div v-if="selectedCampaign" class="lg:col-span-3">
        <div class="bg-white rounded-lg border" style="height: 664px; display: flex; flex-direction: column;">
          <ComponentHeader :title="selectedCampaign.name" :subtitle="selectedCampaign.description">
            <div class="flex gap-2">
              <button
                @click="openEditCampaign"
                class="px-3 py-1 text-sm bg-primary-500 text-white rounded hover:bg-primary-500"
              >
                Edit
              </button>
              <button
                v-if="['draft', 'paused'].includes(selectedCampaign.status)"
                @click="runCampaign(selectedCampaign.id)"
                class="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600"
              >
                Run Campaign
              </button>
              <button
                v-if="selectedCampaign.status === 'active'"
                @click="pauseCampaign(selectedCampaign.id)"
                class="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Pause
              </button>
              <button
                v-if="selectedCampaign.status === 'paused'"
                @click="resumeCampaign(selectedCampaign.id)"
                class="btn-primary-sm"
              >
                Resume
              </button>
            </div>
          </ComponentHeader>

          <!-- Tasks List -->
          <ag-grid-vue
            style="width: 100%; flex: 1;"
            :theme="theme"
            :columnDefs="tasksColumnDefs"
            :rowData="campaignTasks"
            :defaultColDef="defaultColDef"
            :grid-options="gridOptions"
            @grid-ready="onTasksGridReady"
            @row-clicked="onTaskClicked"
          />
        </div>
      </div>
    </div>

    <!-- Task Call History (2 columns wide, below) -->
    <div v-if="selectedCampaignTask" class="mt-6 bg-white rounded-lg border">
      <ComponentHeader title="Call History" :subtitle="`XXXXXXXXXXX XXXXXXXXX - ${selectedCampaignTask.call_attempts} attempt(s)`">
        <button @click="selectedCampaignTask = null" class="text-gray-400 hover:text-gray-600">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ComponentHeader>

      <!-- Scheduled Callback Alert -->
      <div v-if="selectedCampaignTask.call_preferences?.scheduled_callback" class="p-4 border-b" :class="getCallbackAlertClass(selectedCampaignTask.call_preferences.scheduled_callback.datetime)">
        <div class="flex items-start gap-3">
          <svg class="w-6 h-6 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
          </svg>
          <div class="flex-1">
            <h3 class="text-sm font-bold mb-1">Scheduled Callback</h3>
            <div class="text-sm font-semibold mb-1">{{ formatCallbackDateTime(selectedCampaignTask.call_preferences.scheduled_callback.datetime) }}</div>
            <div v-if="selectedCampaignTask.call_preferences.scheduled_callback.notes" class="text-xs italic">{{ selectedCampaignTask.call_preferences.scheduled_callback.notes }}</div>
          </div>
          <button @click="clearCallback(selectedCampaignTask.id)" class="px-2 py-1 text-xs bg-white/50 hover:bg-white/75 rounded">
            Clear
          </button>
        </div>
      </div>

      <!-- Patient Preferences Section -->
      <div v-if="selectedCampaignTask.call_preferences && Object.keys(selectedCampaignTask.call_preferences).length > 0" class="p-4 bg-blue-50 border-b">
        <h3 class="text-sm font-semibold text-gray-900 mb-2">Patient Call Preferences</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div v-if="selectedCampaignTask.call_preferences.preferred_days && selectedCampaignTask.call_preferences.preferred_days.length > 0">
            <div class="text-gray-600 font-medium">Preferred Days</div>
            <div class="text-gray-900">{{ selectedCampaignTask.call_preferences.preferred_days.map(d => d.charAt(0).toUpperCase() + d.slice(1).substring(0, 3)).join(', ') }}</div>
          </div>
          <div v-if="selectedCampaignTask.call_preferences.preferred_hours && selectedCampaignTask.call_preferences.preferred_hours.length > 0">
            <div class="text-gray-600 font-medium">Preferred Hours</div>
            <div class="text-gray-900">{{ selectedCampaignTask.call_preferences.preferred_hours[0].start }} - {{ selectedCampaignTask.call_preferences.preferred_hours[0].end }}</div>
          </div>
          <div v-if="selectedCampaignTask.call_preferences.do_not_call_before">
            <div class="text-gray-600 font-medium">Don't Call Before</div>
            <div class="text-gray-900">{{ selectedCampaignTask.call_preferences.do_not_call_before }}</div>
          </div>
          <div v-if="selectedCampaignTask.call_preferences.do_not_call_after">
            <div class="text-gray-600 font-medium">Don't Call After</div>
            <div class="text-gray-900">{{ selectedCampaignTask.call_preferences.do_not_call_after }}</div>
          </div>
          <div v-if="selectedCampaignTask.call_preferences.timezone">
            <div class="text-gray-600 font-medium">Timezone</div>
            <div class="text-gray-900">{{ selectedCampaignTask.call_preferences.timezone }}</div>
          </div>
          <div v-if="selectedCampaignTask.call_preferences.language">
            <div class="text-gray-600 font-medium">Language</div>
            <div class="text-gray-900">{{ selectedCampaignTask.call_preferences.language.toUpperCase() }}</div>
          </div>
        </div>
      </div>

      <!-- Phone Number Search Results -->
      <div v-if="phoneSearchLoading || phoneSearchResults" class="p-4 bg-green-50 border-b">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-gray-900">Phone Number Search</h3>
          <button
            @click="searchPhoneNumbers(selectedCampaignTask.id)"
            :disabled="phoneSearchLoading"
            class="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {{ phoneSearchLoading ? 'Searching...' : 'Refresh' }}
          </button>
        </div>

        <div v-if="phoneSearchLoading" class="text-xs text-gray-600">
          Searching for phone numbers...
        </div>

        <div v-else-if="phoneSearchResults?.error" class="text-xs text-red-600">
          {{ phoneSearchResults.error }}
        </div>

        <div v-else-if="phoneSearchResults?.numbers_found?.length > 0" class="space-y-2">
          <div class="text-xs">
            <span class="text-gray-600 font-medium">Patient ID:</span>
            <span class="sensitive text-gray-900 ml-2">{{ phoneSearchResults.patient_identifier }}</span>
          </div>
          <div class="text-xs">
            <span class="text-gray-600 font-medium">Selected:</span>
            <span class="sensitive text-green-700 font-semibold ml-2">{{ phoneSearchResults.selected_number }}</span>
            <span class="text-gray-500 ml-2">({{ phoneSearchResults.selection_reason }})</span>
          </div>
          <div v-if="phoneSearchResults.numbers_found.length > 1" class="text-xs">
            <span class="text-gray-600 font-medium">All numbers found ({{ phoneSearchResults.numbers_found.length }}):</span>
            <div class="mt-1 space-y-1">
              <div v-for="num in phoneSearchResults.numbers_found" :key="num.rank" class="ml-2">
                <span class="text-gray-500">{{ num.rank }}.</span>
                <span class="sensitive text-gray-900 ml-1 font-mono">{{ num.number }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="phoneSearchResults?.message" class="text-xs text-amber-600">
          {{ phoneSearchResults.message }}
        </div>
      </div>

      <div class="p-6">
        <div v-if="taskCalls.length === 0" class="text-gray-400 text-center py-8">
          No calls yet
        </div>
        <div v-else class="space-y-4">
          <div v-for="call in taskCalls" :key="call.id" class="border rounded p-4">
            <div class="grid grid-cols-4 gap-4 mb-3">
              <div>
                <div class="text-xs text-gray-600">Status</div>
                <span
                  class="inline-block mt-1 px-2 py-1 rounded text-xs font-semibold"
                  :class="{
                    'bg-yellow-100 text-yellow-800': call.status === 'pending',
                    'bg-blue-100 text-blue-800': ['ringing', 'in_progress'].includes(call.status),
                    'bg-green-100 text-green-800': call.status === 'completed',
                    'bg-red-100 text-red-800': ['failed', 'no_answer', 'busy'].includes(call.status),
                  }"
                >
                  {{ call.status.replace('_', ' ') }}
                </span>
              </div>
              <div>
                <div class="text-xs text-gray-600">Started</div>
                <div class="mt-1 text-sm">{{ formatDateTime(call.started_at) }}</div>
              </div>
              <div>
                <div class="text-xs text-gray-600">Duration</div>
                <div class="mt-1 text-sm">{{ formatDuration(call.duration_seconds) }}</div>
              </div>
              <div>
                <div class="text-xs text-gray-600">External ID</div>
                <div class="mt-1 text-xs font-mono">{{ call.external_call_id || '-' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Campaign Modal -->
    <Teleport to="body">
      <div
        v-if="showCreateForm"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        @click.self="cancelCreateCampaign"
      >
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
          <!-- Modal Header -->
          <div class="flex justify-between items-center p-6 border-b">
            <h2 class="text-xl font-bold">{{ isEditMode ? 'Edit Campaign' : 'Create Campaign' }}</h2>
            <button @click="cancelCreateCampaign" class="text-gray-400 hover:text-gray-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="p-6 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input
                v-model="newCampaign.name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                placeholder="Q1 AWV Outreach Wave 1"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
              <textarea
                v-model="newCampaign.description"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                placeholder="Description of this campaign"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Call Configuration</label>
              <select
                v-model="newCampaign.configuration"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
              >
                <option :value="null">Select configuration...</option>
                <option v-for="config in configurations" :key="config.id" :value="config.id">
                  {{ config.name }}
                </option>
              </select>
            </div>

            <!-- Holdback Configuration -->
            <div class="border-t pt-4 mt-4">
              <h3 class="text-sm font-semibold text-gray-900 mb-4">A/B Testing Configuration (Optional)</h3>

              <div class="mb-4">
                <label class="flex items-center cursor-pointer">
                  <input
                    v-model="newCampaign.holdback_sample"
                    type="checkbox"
                    class="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-blue-dark"
                  />
                  <span class="ml-2 text-sm text-gray-700">Enable holdback sample for lift analysis</span>
                </label>
                <p class="text-xs text-gray-500 mt-1 ml-6">
                  Randomly hold back a percentage of tasks as a control group to measure campaign effectiveness
                </p>
              </div>

              <div v-if="newCampaign.holdback_sample" class="space-y-4 ml-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Holdback Percentage ({{ newCampaign.holdback_config.percentage }}%)
                  </label>
                  <input
                    v-model.number="newCampaign.holdback_config.percentage"
                    type="range"
                    min="0"
                    max="50"
                    step="1"
                    class="w-full"
                  />
                  <div class="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Appointment Type</label>
                  <select
                    v-model="newCampaign.holdback_config.appt_type"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  >
                    <option value="annual_wellness">Annual Wellness Visit</option>
                    <option value="office_visit">Office Visit</option>
                    <option value="preventive">Preventive Care</option>
                    <option value="follow_up">Follow Up</option>
                  </select>
                  <p class="text-xs text-gray-500 mt-1">
                    Type of appointment to track for lift analysis
                  </p>
                </div>
              </div>
            </div>

            <!-- Campaign Settings -->
            <div class="border-t pt-4 mt-4">
              <h3 class="text-sm font-semibold text-gray-900 mb-4">Campaign Settings</h3>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Call Attempts per Task
                  </label>
                  <input
                    v-model.number="newCampaign.max_attempts"
                    type="number"
                    min="1"
                    max="10"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                  <p class="text-xs text-gray-500 mt-1">
                    Number of times to attempt calling each patient before giving up (1-10)
                  </p>
                </div>
              </div>
            </div>

            <!-- Campaign Schedule Configuration -->
            <div class="border-t pt-4 mt-4">
              <h3 class="text-sm font-semibold text-gray-900 mb-4">Campaign Schedule</h3>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    v-model="newCampaign.call_preferences.start_date"
                    type="date"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Days to Call</label>
                  <div class="grid grid-cols-2 gap-2">
                    <label v-for="day in ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']" :key="day" class="flex items-center">
                      <input
                        :value="day"
                        v-model="newCampaign.call_preferences.days_of_week"
                        type="checkbox"
                        class="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-blue-dark"
                      />
                      <span class="ml-2 text-sm text-gray-700 capitalize">{{ day }}</span>
                    </label>
                  </div>
                </div>

                <div v-if="newCampaign.call_preferences && newCampaign.call_preferences.call_hours && newCampaign.call_preferences.call_hours.monday && newCampaign.call_preferences.call_hours.monday[0]">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Call Hours (Default for All Days)</label>
                  <p class="text-xs text-gray-500 mb-2">Set default calling hours. These apply to all selected days.</p>
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">Start Time</label>
                      <input
                        v-model="newCampaign.call_preferences.call_hours.monday[0].start"
                        @input="updateAllCallHours"
                        type="time"
                        class="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">End Time</label>
                      <input
                        v-model="newCampaign.call_preferences.call_hours.monday[0].end"
                        @input="updateAllCallHours"
                        type="time"
                        class="w-full px-2 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="flex gap-3 p-6 border-t bg-gray-50">
            <button @click="cancelCreateCampaign" class="btn-secondary flex-1">
              Cancel
            </button>
            <button @click="isEditMode ? updateCampaign() : createCampaign()" :disabled="!canCreateCampaign" class="btn-primary flex-1">
              {{ isEditMode ? 'Update Campaign' : 'Create Campaign' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Edit Patient Preferences Modal -->
    <Teleport to="body">
      <div
        v-if="showPreferencesForm"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        @click.self="cancelEditPreferences"
      >
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          <!-- Modal Header -->
          <div class="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
            <h2 class="text-xl font-bold">Edit Patient Call Preferences</h2>
            <button @click="cancelEditPreferences" class="text-gray-400 hover:text-gray-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="p-6 space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Preferred Days to Call</label>
              <div class="grid grid-cols-2 gap-2">
                <label v-for="day in ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']" :key="day" class="flex items-center">
                  <input
                    :value="day"
                    v-model="taskPreferences.preferred_days"
                    type="checkbox"
                    class="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-blue-dark"
                  />
                  <span class="ml-2 text-sm text-gray-700 capitalize">{{ day }}</span>
                </label>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Preferred Calling Hours</label>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs text-gray-600 mb-1">Start Time</label>
                  <input
                    v-model="taskPreferences.preferred_hours[0].start"
                    type="time"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
                <div>
                  <label class="block text-xs text-gray-600 mb-1">End Time</label>
                  <input
                    v-model="taskPreferences.preferred_hours[0].end"
                    type="time"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Do Not Call Before</label>
                <input
                  v-model="taskPreferences.do_not_call_before"
                  type="time"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Do Not Call After</label>
                <input
                  v-model="taskPreferences.do_not_call_after"
                  type="time"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <select
                v-model="taskPreferences.timezone"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Phoenix">Arizona Time (MST)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Preferred Language</label>
              <select
                v-model="taskPreferences.language"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="zh">Chinese</option>
                <option value="vi">Vietnamese</option>
                <option value="ko">Korean</option>
                <option value="ru">Russian</option>
              </select>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="flex gap-3 p-6 border-t bg-gray-50 sticky bottom-0">
            <button @click="clearPreferences" class="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
              Clear All
            </button>
            <button @click="cancelEditPreferences" class="btn-secondary flex-1">
              Cancel
            </button>
            <button @click="savePreferences" class="btn-primary flex-1">
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import axios from 'axios'
import { useToast } from 'vue-toastification'
import { standardListTheme } from '@/utils/common'
import TaskActionsCell from './TaskActionsCell.vue'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'

export default {
  name: 'CampaignsTab',
  components: {
    AgGridVue,
    TaskActionsCell,
    PageHeader,
    ComponentHeader,
  },
  data() {
    return {
      theme: standardListTheme,
      gridOptions: {
        suppressCellFocus: true,
        context: {
          componentParent: this
        }
      },
      campaigns: [],
      configurations: [],
      selectedCampaign: null,
      selectedCampaignTask: null,
      campaignTasks: [],
      taskCalls: [],
      phoneSearchResults: null,
      phoneSearchLoading: false,
      showCreateForm: false,
      showPreferencesForm: false,
      isEditMode: false,
      editingCampaignId: null,
      editingTaskId: null,
      campaignGridApi: null,
      tasksGridApi: null,
      pollInterval: null,
      taskPreferences: {
        preferred_days: [],
        preferred_hours: [{ start: '09:00', end: '17:00' }],
        do_not_call_before: '',
        do_not_call_after: '',
        timezone: 'America/New_York',
        language: 'en',
      },
      newCampaign: {
        name: '',
        description: '',
        configuration: null,
        holdback_sample: false,
        holdback_config: {
          percentage: 10.0,
          appt_type: 'annual_wellness',
        },
        max_attempts: 3,
        call_preferences: {
          start_date: '',
          days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          call_hours: {
            monday: [{ start: '09:00', end: '17:00' }],
            tuesday: [{ start: '09:00', end: '17:00' }],
            wednesday: [{ start: '09:00', end: '17:00' }],
            thursday: [{ start: '09:00', end: '17:00' }],
            friday: [{ start: '09:00', end: '17:00' }],
            saturday: [],
            sunday: [],
          },
        },
      },
      campaignColumnDefs: [
        {
          field: 'name',
          headerName: 'Campaign Name',
          flex: 1,
          minWidth: 150,
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 100,
          cellRenderer: (params) => {
            const colors = {
              draft: 'bg-gray-100 text-gray-800',
              active: 'bg-green-100 text-green-800',
              paused: 'bg-yellow-100 text-yellow-800',
              completed: 'bg-blue-100 text-blue-800',
              cancelled: 'bg-red-100 text-red-800',
            }
            return `<span class="px-2 py-1 rounded text-xs font-semibold ${colors[params.value]}">${params.value}</span>`
          },
        },
        {
          headerName: 'Progress',
          width: 90,
          valueGetter: (params) => {
            const completed = params.data.completed_tasks || 0
            const total = params.data.total_tasks || 0
            return `${completed}/${total}`
          },
          cellStyle: (params) => {
            const completed = params.data.completed_tasks || 0
            const total = params.data.total_tasks || 0
            if (total > 0 && completed === total) {
              return { color: '#16a34a', fontWeight: '600' }
            }
            return null
          },
        },
      ],
      tasksColumnDefs: [
        {
          headerName: 'Patient',
          width: 200,
          valueGetter: (params) => {
            const id = params.data.task_metadata?.patient_identifier || ''
            const lastName = 'XXXXXXXX'
            const firstName = 'XXXXXXXXX'
            return `${id} - ${lastName}, ${firstName}`
          },
        },
        {
          field: 'task_metadata.bene_dob',
          headerName: 'DOB',
          width: 110,
          cellClass: 'sensitive',
          valueFormatter: (params) => {
            if (!params.value) return '-'
            const date = new Date(params.value)
            return date.toLocaleDateString()
          },
        },
        {
          field: 'call_preferences',
          headerName: 'Preferences',
          width: 120,
          cellRenderer: (params) => {
            if (!params.value || Object.keys(params.value).length === 0) {
              return '<span class="text-gray-400 text-xs">None</span>'
            }
            const prefs = params.value
            const parts = []
            if (prefs.preferred_days && prefs.preferred_days.length > 0) {
              parts.push(`Days: ${prefs.preferred_days.length}`)
            }
            if (prefs.preferred_hours && prefs.preferred_hours.length > 0) {
              parts.push(`Hours set`)
            }
            if (prefs.language) {
              parts.push(prefs.language.toUpperCase())
            }
            if (prefs.scheduled_callback) {
              parts.push('CB⏰')
            }
            return `<span class="text-xs text-blue-600">${parts.join(' | ')}</span>`
          },
        },
        {
          field: 'outreach_status',
          headerName: 'Status',
          width: 120,
          cellRenderer: (params) => {
            if (!params.value) return '-'
            const colors = {
              pending: 'bg-gray-100 text-gray-800',
              callback_scheduled: 'bg-orange-100 text-orange-800',
              contacted: 'bg-blue-100 text-blue-800',
              appointment_scheduled: 'bg-green-100 text-green-800',
              declined: 'bg-red-100 text-red-800',
              unreachable: 'bg-gray-100 text-gray-600',
              completed: 'bg-purple-100 text-purple-800',
            }
            const displayName = params.value.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            return `<span class="px-2 py-1 rounded text-xs font-semibold ${colors[params.value] || 'bg-gray-100 text-gray-800'}">${displayName}</span>`
          },
        },
        {
          field: 'call_attempts',
          headerName: 'Attempts',
          width: 90,
          valueFormatter: (params) => {
            const attempts = params.value || 0
            const maxAttempts = params.data.max_attempts || 3
            return `${attempts}/${maxAttempts}`
          },
        },
        {
          field: 'last_call_at',
          headerName: 'Last Call',
          width: 140,
          valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : '-',
        },
        {
          headerName: 'Actions',
          width: 70,
          cellRenderer: TaskActionsCell,
          suppressMenu: true,
          sortable: false,
          filter: false,
        },
      ],
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
    }
  },
  computed: {
    toast() {
      return useToast()
    },
    canCreateCampaign() {
      return this.newCampaign.name.trim() !== '' && this.newCampaign.configuration !== null
    },
  },
  async mounted() {
    await Promise.all([this.fetchCampaigns(), this.fetchConfigurations()])
    this.startPolling()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  watch: {
    selectedCampaign(newVal, oldVal) {
      this.$nextTick(() => {
        if (this.campaignGridApi) {
          this.campaignGridApi.sizeColumnsToFit()
        }
      })

      // Restart polling when campaign selection changes
      if (newVal?.id !== oldVal?.id) {
        this.startPolling()
      }
    },
    campaigns() {
      this.$nextTick(() => {
        if (this.campaignGridApi) {
          this.campaignGridApi.sizeColumnsToFit()
        }
      })
    },
    campaignTasks() {
      this.$nextTick(() => {
        if (this.tasksGridApi) {
          this.tasksGridApi.sizeColumnsToFit()
        }
      })
    },
  },
  methods: {
    async fetchCampaigns() {
      try {
        const response = await axios.get('api/outreach/campaigns/')
        this.campaigns = response.data.results || response.data
      } catch (error) {
        console.error('Failed to fetch campaigns:', error)
        this.toast.error('Failed to load campaigns')
      }
    },
    async fetchConfigurations() {
      try {
        const response = await axios.get('api/outreach/call-configurations/', {
          params: { status: 'published' }
        })
        this.configurations = response.data.results || response.data
      } catch (error) {
        console.error('Failed to fetch configurations:', error)
      }
    },
    async fetchCampaignTasks(campaignId) {
      try {
        const response = await axios.get('api/outreach/campaign-tasks/', {
          params: {
            campaign: campaignId,
            ordering: '-added_at'
          }
        })
        this.campaignTasks = response.data.results || response.data
      } catch (error) {
        console.error('Failed to fetch campaign tasks:', error)
        this.toast.error('Failed to load campaign tasks')
      }
    },
    async fetchTaskCalls(campaignTaskId) {
      try {
        const response = await axios.get('api/outreach/calls/', {
          params: {
            campaign_task: campaignTaskId,
            ordering: '-created_at'
          }
        })
        this.taskCalls = response.data.results || response.data
      } catch (error) {
        console.error('Failed to fetch task calls:', error)
        this.toast.error('Failed to load call history')
      }
    },
    onCampaignGridReady(params) {
      this.campaignGridApi = params.api
      params.api.sizeColumnsToFit()
    },
    onTasksGridReady(params) {
      this.tasksGridApi = params.api
      params.api.sizeColumnsToFit()
    },
    async onCampaignClicked(event) {
      this.selectedCampaign = event.data
      this.selectedCampaignTask = null
      await this.fetchCampaignTasks(event.data.id)
      this.startPolling()
    },
    async onTaskClicked(event) {
      this.selectedCampaignTask = event.data
      this.phoneSearchResults = null  // Clear previous results
      await Promise.all([
        this.fetchTaskCalls(event.data.id),
        this.searchPhoneNumbers(event.data.id)
      ])
    },
    async searchPhoneNumbers(taskId) {
      this.phoneSearchLoading = true
      try {
        const response = await axios.get(`api/outreach/campaign-tasks/${taskId}/search_phone_numbers/`)
        this.phoneSearchResults = response.data
      } catch (error) {
        console.error('Failed to search phone numbers:', error)
        this.phoneSearchResults = {
          error: error.response?.data?.error || 'Failed to search phone numbers'
        }
      } finally {
        this.phoneSearchLoading = false
      }
    },
    openCreateCampaign() {
      this.isEditMode = false
      this.editingCampaignId = null
      this.showCreateForm = true
      this.newCampaign = {
        name: '',
        description: '',
        configuration: null,
        holdback_sample: false,
        holdback_config: {
          percentage: 10.0,
          appt_type: 'annual_wellness',
        },
        schedule: {
          start_date: '',
          days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          call_hours: {
            monday: [{ start: '09:00', end: '17:00' }],
            tuesday: [{ start: '09:00', end: '17:00' }],
            wednesday: [{ start: '09:00', end: '17:00' }],
            thursday: [{ start: '09:00', end: '17:00' }],
            friday: [{ start: '09:00', end: '17:00' }],
            saturday: [],
            sunday: [],
          },
        },
      }
    },
    cancelCreateCampaign() {
      this.showCreateForm = false
      this.isEditMode = false
      this.editingCampaignId = null
      this.newCampaign = {
        name: '',
        description: '',
        configuration: null,
        holdback_sample: false,
        holdback_config: {
          percentage: 10.0,
          appt_type: 'annual_wellness',
        },
        schedule: {
          start_date: '',
          days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          call_hours: {
            monday: [{ start: '09:00', end: '17:00' }],
            tuesday: [{ start: '09:00', end: '17:00' }],
            wednesday: [{ start: '09:00', end: '17:00' }],
            thursday: [{ start: '09:00', end: '17:00' }],
            friday: [{ start: '09:00', end: '17:00' }],
            saturday: [],
            sunday: [],
          },
        },
      }
    },
    async createCampaign() {
      try {
        const payload = {
          tenant: 1,
          name: this.newCampaign.name,
          description: this.newCampaign.description,
          configuration: this.newCampaign.configuration,
          holdback_sample: this.newCampaign.holdback_sample,
          holdback_config: this.newCampaign.holdback_config,
          call_preferences: this.newCampaign.call_preferences,
          status: 'draft',
        }

        await axios.post('api/outreach/campaigns/', payload)

        this.toast.success('Campaign created successfully')
        this.showCreateForm = false
        await this.fetchCampaigns()
      } catch (error) {
        console.error('Failed to create campaign:', error)
        this.toast.error('Failed to create campaign')
      }
    },
    openEditCampaign() {
      if (!this.selectedCampaign) return

      this.isEditMode = true
      this.editingCampaignId = this.selectedCampaign.id

      // Ensure schedule has proper structure
      const defaultSchedule = {
        start_date: '',
        days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        call_hours: {
          monday: [{ start: '09:00', end: '17:00' }],
          tuesday: [{ start: '09:00', end: '17:00' }],
          wednesday: [{ start: '09:00', end: '17:00' }],
          thursday: [{ start: '09:00', end: '17:00' }],
          friday: [{ start: '09:00', end: '17:00' }],
          saturday: [],
          sunday: [],
        },
      }

      let callPreferences = defaultSchedule
      if (this.selectedCampaign.call_preferences && Object.keys(this.selectedCampaign.call_preferences).length > 0) {
        callPreferences = {
          start_date: this.selectedCampaign.call_preferences.start_date || '',
          days_of_week: this.selectedCampaign.call_preferences.days_of_week || defaultSchedule.days_of_week,
          call_hours: this.selectedCampaign.call_preferences.call_hours || defaultSchedule.call_hours,
        }
      }

      this.newCampaign = {
        name: this.selectedCampaign.name,
        description: this.selectedCampaign.description || '',
        configuration: this.selectedCampaign.configuration,
        holdback_sample: this.selectedCampaign.holdback_sample || false,
        holdback_config: this.selectedCampaign.holdback_config || {
          percentage: 10.0,
          appt_type: 'annual_wellness',
        },
        call_preferences: callPreferences,
      }
      this.showCreateForm = true
    },
    async updateCampaign() {
      try {
        const payload = {
          name: this.newCampaign.name,
          description: this.newCampaign.description,
          configuration: this.newCampaign.configuration,
          holdback_sample: this.newCampaign.holdback_sample,
          holdback_config: this.newCampaign.holdback_config,
          call_preferences: this.newCampaign.call_preferences,
        }

        await axios.patch(`api/outreach/campaigns/${this.editingCampaignId}/`, payload)

        this.toast.success('Campaign updated successfully')
        this.showCreateForm = false
        await this.fetchCampaigns()

        // Update the selected campaign if it's the one being edited
        if (this.selectedCampaign && this.selectedCampaign.id === this.editingCampaignId) {
          this.selectedCampaign = {
            ...this.selectedCampaign,
            ...payload
          }
        }
      } catch (error) {
        console.error('Failed to update campaign:', error)
        this.toast.error('Failed to update campaign')
      }
    },
    async pauseCampaign(campaignId) {
      try {
        await axios.post(`api/outreach/campaigns/${campaignId}/pause/`, {})
        this.toast.success('Campaign paused')
        await this.fetchCampaigns()
        if (this.selectedCampaign && this.selectedCampaign.id === campaignId) {
          this.selectedCampaign.status = 'paused'
        }
      } catch (error) {
        console.error('Failed to pause campaign:', error)
        this.toast.error('Failed to pause campaign')
      }
    },
    async resumeCampaign(campaignId) {
      try {
        await axios.post(`api/outreach/campaigns/${campaignId}/resume/`, {})
        this.toast.success('Campaign resumed')
        await this.fetchCampaigns()
        if (this.selectedCampaign && this.selectedCampaign.id === campaignId) {
          this.selectedCampaign.status = 'active'
        }
      } catch (error) {
        console.error('Failed to resume campaign:', error)
        this.toast.error('Failed to resume campaign')
      }
    },
    async runCampaign(campaignId) {
      try {
        const response = await axios.post(`api/outreach/campaigns/${campaignId}/run/`, {})
        const { successful, failed, total_eligible } = response.data
        this.toast.success(`Campaign started: ${successful} of ${total_eligible} calls initiated`)

        if (failed > 0) {
          this.toast.warning(`${failed} calls failed to start`)
        }

        await this.fetchCampaigns()
        await this.fetchCampaignTasks(campaignId)

        if (this.selectedCampaign && this.selectedCampaign.id === campaignId) {
          this.selectedCampaign.status = 'active'
        }
      } catch (error) {
        console.error('Failed to run campaign:', error)
        const errorMsg = error.response?.data?.error || 'Failed to run campaign'
        this.toast.error(errorMsg)
      }
    },
    updateAllCallHours() {
      // Update all days with the same hours as monday (the reference day)
      if (!this.newCampaign.call_preferences?.call_hours?.monday?.[0]) {
        return
      }
      const referenceHours = this.newCampaign.call_preferences.call_hours.monday[0]
      const days = ['tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      days.forEach(day => {
        this.newCampaign.call_preferences.call_hours[day] = [{ ...referenceHours }]
      })
    },
    formatDateTime(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString()
    },
    formatDuration(seconds) {
      if (!seconds) return '-'
      const minutes = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    },
    formatCallbackDateTime(datetime) {
      if (!datetime) return ''
      const date = new Date(datetime)
      const now = new Date()
      const isToday = date.toDateString() === now.toDateString()
      const tomorrow = new Date(now)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const isTomorrow = date.toDateString() === tomorrow.toDateString()

      let prefix = ''
      if (isToday) prefix = 'Today at '
      else if (isTomorrow) prefix = 'Tomorrow at '

      return prefix + date.toLocaleString()
    },
    getCallbackAlertClass(datetime) {
      if (!datetime) return ''
      const date = new Date(datetime)
      const now = new Date()
      const isPast = date < now
      const isToday = date.toDateString() === now.toDateString()

      if (isPast) return 'bg-red-50 border-l-4 border-red-500 text-red-900'
      if (isToday) return 'bg-orange-50 border-l-4 border-orange-500 text-orange-900'
      return 'bg-green-50 border-l-4 border-green-500 text-green-900'
    },
    async clearCallback(taskId) {
      if (!confirm('Clear the scheduled callback for this task?')) return

      try {
        const task = this.campaignTasks.find(t => t.id === taskId)
        const updatedPrefs = { ...task.call_preferences }
        delete updatedPrefs.scheduled_callback

        await axios.patch(`api/outreach/campaign-tasks/${taskId}/`, {
          call_preferences: updatedPrefs,
          outreach_status: 'pending'
        })
        this.toast.success('Callback cleared')
        await this.fetchCampaignTasks(this.selectedCampaign.id)
        if (this.selectedCampaignTask?.id === taskId) {
          await this.fetchTaskCalls(taskId)
        }
      } catch (error) {
        console.error('Failed to clear callback:', error)
        this.toast.error('Failed to clear callback')
      }
    },
    startPolling() {
      this.stopPolling()

      // Poll every 5 seconds if a campaign is selected
      if (this.selectedCampaign) {
        this.pollInterval = setInterval(() => {
          this.fetchCampaigns()
          if (this.selectedCampaign) {
            this.fetchCampaignTasks(this.selectedCampaign.id)
          }
          if (this.selectedCampaignTask) {
            this.fetchTaskCalls(this.selectedCampaignTask.id)
          }
        }, 5000)
      }
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },
    async runSingleTask(taskId) {
      try {
        const task = this.campaignTasks.find(t => String(t.id) === String(taskId))
        if (!task) return

        // Allow manual run regardless of retry settings - let backend handle validation
        await axios.post(`api/outreach/campaign-tasks/${taskId}/run/`, {})

        this.toast.success('Call started')
        await this.fetchCampaignTasks(this.selectedCampaign.id)
        if (this.selectedCampaignTask?.id === taskId) {
          await this.fetchTaskCalls(taskId)
        }
      } catch (error) {
        console.error('Failed to start call:', error)
        const errorMsg = error.response?.data?.error || 'Failed to start call'
        this.toast.error(errorMsg)
      }
    },
    async deleteSingleTask(taskId) {
      if (!confirm('Are you sure you want to remove this task from the campaign?')) {
        return
      }

      try {
        await axios.delete(`api/outreach/campaign-tasks/${taskId}/`)
        this.toast.success('Task removed from campaign')

        if (this.selectedCampaignTask?.id === taskId) {
          this.selectedCampaignTask = null
        }

        await this.fetchCampaigns()
        await this.fetchCampaignTasks(this.selectedCampaign.id)
      } catch (error) {
        console.error('Failed to delete task:', error)
        this.toast.error('Failed to remove task')
      }
    },
    openEditPreferences(taskId) {
      const task = this.campaignTasks.find(t => String(t.id) === String(taskId))
      if (!task) return

      this.editingTaskId = taskId

      // Load existing preferences or use defaults
      const prefs = task.call_preferences || {}
      this.taskPreferences = {
        preferred_days: prefs.preferred_days || [],
        preferred_hours: prefs.preferred_hours || [{ start: '09:00', end: '17:00' }],
        do_not_call_before: prefs.do_not_call_before || '',
        do_not_call_after: prefs.do_not_call_after || '',
        timezone: prefs.timezone || 'America/New_York',
        language: prefs.language || 'en',
      }

      this.showPreferencesForm = true
    },
    cancelEditPreferences() {
      this.showPreferencesForm = false
      this.editingTaskId = null
      this.taskPreferences = {
        preferred_days: [],
        preferred_hours: [{ start: '09:00', end: '17:00' }],
        do_not_call_before: '',
        do_not_call_after: '',
        timezone: 'America/New_York',
        language: 'en',
      }
    },
    clearPreferences() {
      this.taskPreferences = {
        preferred_days: [],
        preferred_hours: [{ start: '09:00', end: '17:00' }],
        do_not_call_before: '',
        do_not_call_after: '',
        timezone: 'America/New_York',
        language: 'en',
      }
    },
    async savePreferences() {
      if (!this.editingTaskId) return

      try {
        // Get existing call_preferences to preserve scheduled_callback if it exists
        const task = this.campaignTasks.find(t => t.id === this.editingTaskId)
        const existingPrefs = task.call_preferences || {}

        // Clean up empty values and build new preferences
        const cleanPrefs = {}
        if (this.taskPreferences.preferred_days.length > 0) {
          cleanPrefs.preferred_days = this.taskPreferences.preferred_days
        }
        if (this.taskPreferences.preferred_hours[0].start && this.taskPreferences.preferred_hours[0].end) {
          cleanPrefs.preferred_hours = this.taskPreferences.preferred_hours
        }
        if (this.taskPreferences.do_not_call_before) {
          cleanPrefs.do_not_call_before = this.taskPreferences.do_not_call_before
        }
        if (this.taskPreferences.do_not_call_after) {
          cleanPrefs.do_not_call_after = this.taskPreferences.do_not_call_after
        }
        if (this.taskPreferences.timezone) {
          cleanPrefs.timezone = this.taskPreferences.timezone
        }
        if (this.taskPreferences.language) {
          cleanPrefs.language = this.taskPreferences.language
        }

        // Preserve scheduled_callback if it exists
        if (existingPrefs.scheduled_callback) {
          cleanPrefs.scheduled_callback = existingPrefs.scheduled_callback
        }

        await axios.patch(`api/outreach/campaign-tasks/${this.editingTaskId}/`, {
          call_preferences: cleanPrefs
        })

        this.toast.success('Preferences saved successfully')
        this.showPreferencesForm = false
        await this.fetchCampaignTasks(this.selectedCampaign.id)
      } catch (error) {
        console.error('Failed to save preferences:', error)
        this.toast.error('Failed to save preferences')
      }
    },
  },
}
</script>

<style scoped>
@reference "../../assets/theme.css";
.container-page {
  @apply p-6;
}

.heading-page {
  @apply text-3xl font-bold text-gray-900;
}
</style>
